import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { getValueOfAnimatableColorProperty } from '~/_shared/types/animation/animatableProperty.helpers';
import { type Proximity } from '~/_shared/types/proximity/proximity.types';
import {
  registerAnimationCallback, removeAnimationCallback,
} from '~/_shared/utils/colorAnimationTicker/colorAnimationTicker';
import { changeProximityStyle } from '~/_shared/utils/proximity';
import { activeBoundaryBorderAnimationParams } from '~/map/map/boundary/boundaryView/mapBoundaryStyles/useMapBoundaryExtraStyles';
import { useProximityStates } from './useProximityStates';

export const useProximityExtraStyles = <T extends Proximity>(proximity: T) => {
  const { isActive, isHovered } = useProximityStates(proximity);
  const [currentActiveBorderColor, setCurrentActiveBorderColor] = useState<string>();

  const proximityStyleRef = useRef(proximity.styles);
  proximityStyleRef.current = proximity.styles;

  const computeActiveBorderColor = useCallback((animationPercent: number) => {
    const color = getValueOfAnimatableColorProperty({
      value: proximityStyleRef.current.borderColor,
      animation: activeBoundaryBorderAnimationParams,
    }, animationPercent);
    setCurrentActiveBorderColor(color);
  }, []);

  useEffect(() => {
    registerAnimationCallback(computeActiveBorderColor);

    return () => {
      removeAnimationCallback(computeActiveBorderColor);
    };
  }, [computeActiveBorderColor]);

  const currentBorderColor = isActive ? currentActiveBorderColor : undefined;

  return useMemo(() => {
    return changeProximityStyle(proximity, isHovered, isActive, currentBorderColor);
  }, [isActive, isHovered, currentBorderColor, proximity]);
};
